import { useTranslation } from 'tools/i18n';
import { makeStyles } from '@material-ui/styles';
import { ShopwareDeliveryTime, ShopwareUuid } from '@services/types/Shopware';
import useApplicationStore from '@services/stores/ApplicationStore';
import { DeliveryTimeAlternativeName, resolveIndicator } from '@pages/api/properties/labels';
import { useEffect, useState } from 'react';

const useStyles = makeStyles(() => ({
    stock: {
        color: 'inherit',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 'inherit',
        fontWeight: 300,
        letterSpacing: 0,
        lineHeight: 1,
        marginBottom: 2,
        fontSmoothing: 'auto',
        '&.indicator': {
            '&:before': {
                content: '""',
                height: '8px',
                width: '8px',
                borderRadius: '50%',
            },
            '&.in-stock:before': {
                backgroundColor: 'rgb(78, 199, 145)',
            },
            '&.black:before': {
                backgroundColor: 'black',
            },
            '&.out-of-stock:before': {
                backgroundColor: 'red',
            },
            '&.incoming:before': {
                backgroundColor: 'rgb(78, 199, 145)',
            },
        }
    },
}), { name: 'StockIndicator' });

interface StockIndicatorProps {
    show?: boolean;
    deliveryTime?: ShopwareDeliveryTime | null;
    deliveryTimeId?: ShopwareUuid;
    style?: React.CSSProperties;
    className?: string | null;
    alternativeName?: DeliveryTimeAlternativeName;
}

export default function StockIndicator(
    {
        show = true,
        deliveryTime,
        deliveryTimeId = undefined,
        style = undefined,
        className = null,
        alternativeName,
    }: StockIndicatorProps,
) {
    const { getDeliveryTime } = useApplicationStore(state => state.methods);
    const classes = useStyles();
    const classNames = `${classes.stock} ${className}`;
    const [ id, setId ] = useState<ShopwareUuid | null>(null);

    useEffect(() => {
        const id = deliveryTimeId ? deliveryTimeId : deliveryTime?.id;
        if (id) {
            setId(id);
        }
    }, [deliveryTimeId, deliveryTime?.id]);

    if (!show) {
        return null;
    }

    if (!id && deliveryTime?.unit && deliveryTime?.name) {
        let indicator = resolveIndicator(deliveryTime);

        return <span style={style} className={`${classNames} ${indicator} ${indicator ? 'indicator' : ''}`}>
            {deliveryTime.name}
        </span>;
    } else if (!id) {
        return null;
    }

    const internalDeliveryTime = getDeliveryTime(id);
    let name = internalDeliveryTime?.name;

    if (alternativeName) {
        name = internalDeliveryTime?.alternativeNames[alternativeName];
    }

    if (internalDeliveryTime) {
        let indicator = internalDeliveryTime.indicator;
        return <span style={style} className={`${classNames} ${indicator} ${indicator ? 'indicator' : ''}`}>
            {name}
        </span>;
    }

    return <>
        <span style={style} className={classNames}></span>
    </>;
}
